import type { SubscriptionStatus } from './types';

/**
 * Canonical card-brand display labels. Stripe sends these as lowercase
 * keys; we render them with proper capitalization.
 */
const BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  jcb: 'JCB',
  diners: 'Diners Club',
  unionpay: 'UnionPay',
  unknown: 'Card',
};

export function formatCardBrand(brand: string | null | undefined): string {
  if (!brand) return 'Card';
  return BRAND_LABELS[brand.toLowerCase()] ?? brand;
}

/**
 * Phase-8 ACH: the stripe-webhook reconciliation stores a saved US bank
 * account in payment_methods with brand='us_bank_account' (or the bank's
 * display name) and the far-future placeholder expiry 2099. A payment method
 * is a bank account when its brand is the us_bank_account token OR its exp_year
 * is the bank placeholder (banks don't expire).
 */
const ACH_BRAND_TOKEN = 'us_bank_account';
const ACH_PLACEHOLDER_YEAR = 2099;

export function isBankPaymentMethod(pm: {
  brand: string | null | undefined;
  exp_year?: number | null;
}): boolean {
  if (pm.brand?.toLowerCase() === ACH_BRAND_TOKEN) return true;
  return pm.exp_year === ACH_PLACEHOLDER_YEAR;
}

/**
 * Render a saved payment method as a single human label, e.g.
 *   card → "Visa •••• 4242"
 *   bank → "Bank •••• 6789" (or "STRIPE TEST BANK •••• 6789")
 * The bullet glyph matches the existing payment-methods route ("····").
 */
export function formatPaymentMethodLabel(pm: {
  brand: string | null | undefined;
  last4: string;
  exp_year?: number | null;
}): string {
  if (isBankPaymentMethod(pm)) {
    const name = pm.brand && pm.brand.toLowerCase() !== ACH_BRAND_TOKEN ? pm.brand : 'Bank account';
    return `${name} ···· ${pm.last4}`;
  }
  return `${formatCardBrand(pm.brand)} ···· ${pm.last4}`;
}

/**
 * Display the expiration as MM/YY. Stripe gives us 4-digit years; we
 * truncate to the last two digits to match what's printed on the card.
 */
export function formatExpiration(month: number, year: number): string {
  const m = month.toString().padStart(2, '0');
  const y = (year % 100).toString().padStart(2, '0');
  return `${m}/${y}`;
}

/**
 * Human-readable subscription status. Used by the parent /auto-pay route
 * and the admin subscriptions list.
 */
export function subscriptionStatusLabel(status: SubscriptionStatus): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'trialing':
      return 'In trial';
    case 'past_due':
      return 'Payment past due';
    case 'unpaid':
      return 'Unpaid';
    case 'canceled':
      return 'Canceled';
    case 'incomplete':
      return 'Incomplete';
    case 'incomplete_expired':
      return 'Expired';
    case 'paused':
      return 'Paused';
    default: {
      // Exhaustiveness check — if a new status appears in the enum, the
      // TS compiler will flag this never-assignment as an error.
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

/**
 * A subscription is "active" (collecting / about to collect) when it's
 * in any of these states. past_due is included because we want to show
 * the parent a payment-retry prompt — the auto-pay isn't off, it just
 * needs attention.
 */
export function isSubscriptionActive(status: SubscriptionStatus): boolean {
  return status === 'active' || status === 'trialing' || status === 'past_due';
}

/**
 * For the parent dashboard: should we show a "Retry payment" banner?
 * Yes when past_due / unpaid; the failure record will tell the parent why.
 */
export function needsPaymentAttention(status: SubscriptionStatus): boolean {
  return status === 'past_due' || status === 'unpaid' || status === 'incomplete';
}
